// packages/shared/src/schemas/command.ts
// Hoisted slash command Zod schemas — single source of truth for both
// guardian-worker (server-side validation) and SPA (client-side validation).
//
// Mirrors the Phase 3 pattern: packages/shared/src/schemas/embed.ts and
// packages/shared/src/schemas/reminder.ts are the reference.
//
// Schema contract:
//   createCommandSchema: POST + PUT (full replace) — name + description + type + embedJson + enabled
//   updateCommandSchema: alias for createCommandSchema (full replace via PUT)
//   patchCommandSchema:  PATCH — enabled toggle only
//
// Phase 7 (plan 07-00) extension:
//   - Added optional `type` field with enum ['static','dynamic','utility','admin']
//     (default 'static' — every existing caller continues to work).
//   - embedJson is now nullable + optional. A superRefine enforces the existing
//     required-when-static rule. Dynamic / utility / admin commands will have
//     handlers that compute their own embeds at runtime; the row's embed_json
//     column stores either the static JSON or '' (set by 07-04 on insert).
//   - validateEmbedJsonString remains exported as a separate strict helper for
//     guardian-worker route reuse — when type === 'static' && embedJson is set,
//     the route runs JSON.parse → embedSchema → JSON.stringify on the value.
//
// Discord slash command name rules (per Discord API docs):
//   - 1-32 characters
//   - Lowercase a-z, digits 0-9, hyphens, underscores ONLY
//   - Regex: /^[a-z0-9_-]{1,32}$/

import { z } from 'zod';
import { embedSchema } from './embed';

const SLASH_NAME_RE = /^[a-z0-9_-]{1,32}$/;

/**
 * Command type discriminator. Mirrors the slash_commands.type enum on the row.
 * 'static'  = D1 embed_json + zero handler logic (every pre-Phase-7 row).
 * 'dynamic' = handler computes embed at runtime (e.g. /next-raid, /membercount).
 * 'utility' = handler executes a side-effect and returns its own embed (/role-toggle).
 * 'admin'   = officer-gated commands (/reload, etc).
 */
export const COMMAND_TYPE = z.enum(['static', 'dynamic', 'utility', 'admin']);
export type CommandType = z.infer<typeof COMMAND_TYPE>;

/**
 * Strict embed-JSON validator. Used by guardian-worker for static-row writes:
 * runs JSON.parse → embedSchema (shape + 6000-char total) → re-stringify so the
 * D1 row stores a canonical form. SPA forms keep the embed as an object in form
 * state and only stringify at the onSubmit boundary translator (Pattern 7).
 *
 * Plan 07-04 wires this helper into the worker's commands route — it runs only
 * when `type === 'static' && embedJson` is set (dynamic/utility/admin rows skip
 * the pipeline because their handlers compute embeds at runtime).
 */
export const validateEmbedJsonString = z
  .string()
  .transform((s, ctx) => {
    try {
      return JSON.parse(s);
    } catch {
      ctx.addIssue({ code: 'custom', message: 'embedJson must be valid JSON' });
      return z.NEVER;
    }
  })
  .pipe(embedSchema)
  .transform((v) => JSON.stringify(v));

/**
 * Full create/replace schema — used for POST and PUT /api/commands(/:id).
 *
 * Phase 7 (plan 07-00): embedJson is no longer eagerly transformed via
 * validateEmbedJsonString. Static rows still require it (enforced by the
 * superRefine below); dynamic/utility/admin rows may submit null or omit it.
 * The route handler (extended in plan 07-04) runs validateEmbedJsonString
 * separately when type === 'static' && embedJson is set.
 */
export const createCommandSchema = z
  .object({
    name: z.string().regex(SLASH_NAME_RE, 'Lowercase letters, digits, _, - only (1-32 chars)'),
    description: z.string().min(1, 'Description is required').max(100, 'Description max 100 chars'),
    type: COMMAND_TYPE.optional().default('static'),
    embedJson: z.string().nullable().optional(),
    enabled: z.boolean().optional().default(true),
  })
  .superRefine((val, ctx) => {
    if (val.type === 'static' && (val.embedJson === null || val.embedJson === undefined || val.embedJson === '')) {
      ctx.addIssue({
        code: 'custom',
        path: ['embedJson'],
        message: 'embedJson required for static commands',
      });
    }
  });

/** Full replace schema — identical to createCommandSchema (PUT semantics). */
export const updateCommandSchema = createCommandSchema;

/** Partial update schema — used for PATCH /api/commands/:id (enabled toggle only). */
export const patchCommandSchema = z.object({
  enabled: z.boolean(),
});

/** Input type for POST/PUT body (before embedJson transformation). */
export type CommandWriteBody = z.input<typeof createCommandSchema>;

/** Output type after full schema transformation (used internally by worker). */
export type CommandWriteOutput = z.output<typeof createCommandSchema>;

/** Patch body type (enabled only). */
export type CommandPatchBody = z.infer<typeof patchCommandSchema>;
